import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BRAND_TEMPLATE_MAX_LABEL,
  BrandTemplateKind,
  type BrandWorkspaceTemplate,
  type Tenant,
} from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useIsWorkspaceAdmin } from '../../auth/useWorkspaceAdmin';
import { useProxyImage } from '../../lib/useProxyImage';
import { Card, CardBody, CardHeader } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Field';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/States';
import { TemplateMappingModal } from './TemplateMappingModal';
import { TemplateExportGuideModal } from './TemplateExportGuideModal';

/** What each kind is called, what it accepts, and how someone produces one. */
const KINDS: {
  kind: BrandTemplateKind;
  title: string;
  accept: string;
  /** Told to the person BEFORE they open a file picker, so the export step happens first. */
  how: string;
}[] = [
  {
    kind: BrandTemplateKind.FIGMA_SVG,
    title: 'SVG templates',
    accept: 'image/svg+xml,.svg',
    how: 'Import your single frame SVG template files from Figma, Adobe Illustrator or another SVG editor, for use as reference in the Generate Design tool.',
  },
  {
    kind: BrandTemplateKind.CANVA_PPTX,
    title: 'PPTX templates',
    accept:
      'application/vnd.openxmlformats-officedocument.presentationml.presentation,.pptx',
    how: 'Import your single frame PPTX template files from Canva, PowerPoint or another PPTX editor, for use as a reference in the Generate Design tool.',
  },
];

/**
 * The design templates a Brand Workspace keeps, which Generate Design fills with new content.
 *
 * Per workspace, not per install: a template carries a brand's typography and colour, so the team
 * running one brand has no business seeing another's layouts. That is the same boundary approval
 * modes follow, and the reason the row is scoped to the Tenant.
 *
 * Two sections rather than one list with a filter, because the two FORMATS are not interchangeable
 * in practice — an .svg keeps real text and a .pptx is the only thing Canva turns back into
 * editable text — and a single mixed list would make an empty half look like a mistake. Neither
 * belongs to one tool: an .svg comes out of Illustrator as readily as Figma.
 *
 * Every action here saves at once (there is no form to save), so it never joins a page's Save
 * footer. The optional props are the Brand Workspace v2 Workflow page's: `canEdit` by the workspace
 * being edited (E20; default today's useIsWorkspaceAdmin), and `onSaved`, called after an upload, a
 * rename, a delete or a mapping saves, where that page confirms with its toast (O4).
 */
export function WorkspaceTemplatesSection({
  workspace,
  canEdit: canEditProp,
  onSaved,
}: {
  workspace: Tenant;
  /** Who may upload, map, rename and delete. Default: today's rule, useIsWorkspaceAdmin. */
  canEdit?: boolean;
  /** Called after a template change has been saved. */
  onSaved?: () => void;
}) {
  const isWorkspaceAdmin = useIsWorkspaceAdmin();
  const canEdit = canEditProp ?? isWorkspaceAdmin;
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  /**
   * The template being mapped. ONE dialog for the whole section rather than one per card: a mapping
   * dialog holds a blob URL over the rendered template, and thirty mounted copies would pin thirty
   * of them.
   */
  const [mapping, setMapping] = useState<BrandWorkspaceTemplate | null>(null);
  /**
   * Which kind's export guide is open. One dialog for the whole section, like {@link mapping} —
   * two mounted copies of the same instructions is two things to keep in step.
   */
  const [guideKind, setGuideKind] = useState<BrandTemplateKind | null>(null);

  const templates = useQuery({
    queryKey: ['brand-templates', workspace.id],
    queryFn: ({ signal }) => api.listBrandTemplates(undefined, signal),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['brand-templates', workspace.id] });
  /** An upload, rename or delete worked: read the list again, and tell the page. */
  const changed = () => {
    void invalidate();
    onSaved?.();
  };

  return (
    <Card>
      <CardHeader
        title="Design templates"
        description="Reusable layouts Generate Design fills with new copy and imagery. Templates stay in this workspace."
      />
      <CardBody className="space-y-6">
        {KINDS.map((spec) => (
          <TemplateKindSection
            key={spec.kind}
            spec={spec}
            canEdit={canEdit}
            loading={templates.isLoading}
            templates={(templates.data ?? []).filter((t) => t.kind === spec.kind)}
            onChanged={changed}
            onError={setError}
            onMap={setMapping}
            onShowGuide={() => setGuideKind(spec.kind)}
          />
        ))}

        {error && <p className="text-sm text-red-600 dark:text-error-dark">{error}</p>}

        {/* Said once, at the bottom, rather than on both upload buttons. */}
        <p className="border-t border-line-soft pt-4 text-xs text-ink-muted">
          Templates are inputs to generation — they are not posted anywhere, so they stay out of the
          Media Library. Up to {BRAND_TEMPLATE_MAX_LABEL} each.
        </p>
      </CardBody>

      <TemplateExportGuideModal
        open={guideKind !== null}
        kind={guideKind}
        onClose={() => setGuideKind(null)}
      />

      <TemplateMappingModal
        template={mapping}
        onClose={() => setMapping(null)}
        onSaved={() => {
          invalidate();
          // Also drop the annotated read: a saved mapping does not change the markup, but the
          // template row it was seeded from has, and a stale seed would reopen with the old map.
          void queryClient.invalidateQueries({ queryKey: ['brand-template'] });
          onSaved?.();
        }}
      />
    </Card>
  );
}

function TemplateKindSection({
  spec,
  templates,
  canEdit,
  loading,
  onChanged,
  onError,
  onMap,
  onShowGuide,
}: {
  spec: (typeof KINDS)[number];
  templates: BrandWorkspaceTemplate[];
  canEdit: boolean;
  loading: boolean;
  onChanged: () => void;
  onError: (message: string | null) => void;
  onMap: (template: BrandWorkspaceTemplate) => void;
  /** Opens the shared export guide for THIS section's format. */
  onShowGuide: () => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadBrandTemplate({ file, kind: spec.kind }),
    onSuccess: () => {
      onError(null);
      onChanged();
    },
    onError: (err) =>
      // The server's messages name the slide count, the export menu, the size to re-export at. They
      // are the whole value of the validation, so they are shown verbatim rather than replaced.
      onError(
        err instanceof ApiRequestError ? err.message : `Could not upload that ${spec.title.toLowerCase()} file.`,
      ),
  });

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-ink-strong">{spec.title}</h3>
          <p className="mt-0.5 max-w-xl text-xs text-ink-muted">
            {spec.how}{' '}
            {/* Inside the sentence, where the export is being described — the settings that make a
                template usable are exactly what someone is missing at this moment. */}
            <button
              type="button"
              onClick={onShowGuide}
              className="focus-ring rounded font-medium text-link hover:text-action-hover hover:underline"
            >
              View Export Instructions
            </button>
          </p>
        </div>
        {canEdit && (
          <>
            <Button
              variant="secondary"
              loading={upload.isPending}
              onClick={() => fileInput.current?.click()}
            >
              Upload template
            </Button>
            <input
              ref={fileInput}
              type="file"
              accept={spec.accept}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Cleared before the upload starts, so picking the same file twice after a failure
                // still fires a change event.
                e.target.value = '';
                if (file) upload.mutate(file);
              }}
            />
          </>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-ink-muted">Loading…</p>
      ) : templates.length === 0 ? (
        <EmptyState
          title="No templates yet"
          description={`Upload a ${spec.kind === BrandTemplateKind.FIGMA_SVG ? 'Figma SVG' : 'Canva PowerPoint'} export to use it as a design template.`}
        />
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              canEdit={canEdit}
              onChanged={onChanged}
              onError={onError}
              onMap={onMap}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function TemplateCard({
  template,
  canEdit,
  onChanged,
  onError,
  onMap,
}: {
  template: BrandWorkspaceTemplate;
  canEdit: boolean;
  onChanged: () => void;
  onError: (message: string | null) => void;
  onMap: (template: BrandWorkspaceTemplate) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(template.nickname);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  /**
   * An SVG is its own thumbnail; a `.pptx` has one only if someone supplied it.
   *
   * Both go through the proxy hook because the file sits behind the bearer guard — a plain `<img src>`
   * on an API path renders a broken image, which is a trap this codebase has already fallen into.
   */
  const thumbSource =
    template.kind === BrandTemplateKind.FIGMA_SVG ? template.fileUrl : template.previewUrl;
  const thumb = useProxyImage(thumbSource);

  const rename = useMutation({
    mutationFn: () => api.updateBrandTemplate(template.id, { nickname: name }),
    onSuccess: () => {
      setRenaming(false);
      onError(null);
      onChanged();
    },
    onError: (err) =>
      onError(err instanceof ApiRequestError ? err.message : 'Could not rename that template.'),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteBrandTemplate(template.id),
    onSuccess: () => {
      onError(null);
      onChanged();
    },
    onError: (err) =>
      onError(err instanceof ApiRequestError ? err.message : 'Could not delete that template.'),
  });

  return (
    <li className="overflow-hidden rounded-xl border border-line bg-card">
      <div className="flex aspect-[4/3] items-center justify-center bg-page">
        {thumb ? (
          // object-contain, never cover: a template's whole point is its composition, and cropping
          // the preview hides the margins someone deliberately set.
          // bg-white on the image itself: a transparent template stays legible on a dark card. The image
          // is sized to the picture (max-h/max-w, centred by the tile), so the white never fills the
          // letterbox around a picture of another shape.
          <img src={thumb} alt={template.nickname} className="max-h-full max-w-full bg-white object-contain" />
        ) : (
          <span className="px-3 text-center text-xs text-ink-faint">
            {template.kind === BrandTemplateKind.CANVA_PPTX
              ? 'PowerPoint — no preview'
              : 'Preview unavailable'}
          </span>
        )}
      </div>

      <div className="space-y-2 p-3">
        {renaming ? (
          <div className="space-y-2">
            <Input
              value={name}
              autoFocus
              aria-label="Template name"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && name.trim()) rename.mutate();
                if (e.key === 'Escape') {
                  setName(template.nickname);
                  setRenaming(false);
                }
              }}
            />
            <div className="flex gap-2">
              <Button size="sm" loading={rename.isPending} disabled={!name.trim()} onClick={() => rename.mutate()}>
                Save
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setName(template.nickname);
                  setRenaming(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <p className="truncate text-sm font-medium text-ink" title={template.nickname}>
            {template.nickname}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-1.5">
          {template.widthPx && template.heightPx && (
            <span className="text-xs text-ink-muted">
              {template.widthPx}×{template.heightPx}
            </span>
          )}
          {/* The mapping state, stated plainly — a template that looks ready but isn't mapped would
              generate a design with the copy in the wrong place. */}
          {template.staticOnly ? (
            <Badge tone="neutral">Static background</Badge>
          ) : template.roleMap && Object.keys(template.roleMap).length > 0 ? (
            <Badge tone="green">Mapped</Badge>
          ) : (
            <Badge tone="amber">Needs mapping</Badge>
          )}
        </div>

        {canEdit && !renaming && (
          <div className="flex flex-wrap gap-2 pt-1">
            {/* A static background has no text to swap, so there is nothing to map and the button
                would only lead to an empty dialog. */}
            {!template.staticOnly && (
              <Button size="sm" variant="ghost" onClick={() => onMap(template)}>
                {template.roleMap && Object.keys(template.roleMap).length > 0 ? 'Edit mapping' : 'Map'}
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setRenaming(true)}>
              Rename
            </Button>
            {confirmingDelete ? (
              <>
                <Button size="sm" variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
                  Delete
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmingDelete(false)}>
                  Keep
                </Button>
              </>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setConfirmingDelete(true)}>
                Delete
              </Button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}
